// src/oidc/pkce.js — PKCE helpers (RFC 7636)
//
// Provides cryptographic utilities for the OIDC Authorization Code Flow with PKCE.
// These are browser-only utilities that rely on the Web Crypto API (window.crypto).

/**
 * Generate a cryptographically random code verifier for PKCE.
 *
 * Creates a 64-byte random value and returns it as a base64url-encoded string.
 *
 * @returns {string} A base64url-encoded code verifier
 */
export function generateCodeVerifier() {
  const array = new Uint8Array(64)
  window.crypto.getRandomValues(array)
  return base64URLEncode(array)
}

/**
 * Encode a buffer as a base64url string (RFC 4648 §5).
 *
 * Uses a chunked approach to avoid stack overflow when processing
 * large buffers via String.fromCharCode.apply().
 *
 * @param {Uint8Array|ArrayBuffer} buffer - The binary data to encode
 * @returns {string} Base64url-encoded string (without padding)
 */
function base64URLEncode(buffer) {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer
  const CHUNK_SIZE = 0x8000
  let base64 = ''
  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    base64 += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK_SIZE))
  }
  return btoa(base64).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Generate a cryptographically random state parameter for OIDC CSRF protection.
 *
 * Creates a 16-byte random value and returns it as a base64url-encoded string.
 *
 * @returns {string} A base64url-encoded state string
 */
export function generateState() {
  const array = new Uint8Array(16)
  window.crypto.getRandomValues(array)
  return base64URLEncode(array)
}

/**
 * Generate a PKCE code challenge from a code verifier.
 *
 * Computes the SHA-256 hash of the verifier and returns it as a base64url-encoded string,
 * implementing the S256 challenge method defined in RFC 7636.
 *
 * @param {string} verifier - The code verifier string
 * @returns {Promise<string>} A base64url-encoded SHA-256 hash of the verifier
 */
export async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder()
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    encoder.encode(verifier)
  )
  return base64URLEncode(digest)
}



// WEBPACK FOOTER //
// ./src/oidc/pkce.js